import { useEffect, useState } from 'react'
import { CalendarDays, ClipboardCheck, Home, LogOut, PackageSearch } from 'lucide-react'
import { NavLink, Navigate, Outlet, useLocation } from 'react-router-dom'

import { apiClient } from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'

export interface ClientPortalContext {
  customer_id: number
  customer_name: string
  customer_code: string
  role: string
  logo_url?: string | null
  site_ids?: number[] | null
}

const links = [
  { to: '/client', label: 'Inicio', icon: Home, end: true },
  { to: '/client/intercambios', label: 'Intercambios y agenda', icon: CalendarDays },
  { to: '/client/prendas', label: 'Mis prendas', icon: PackageSearch },
  { to: '/client/reportes', label: 'Resultados y reportes', icon: ClipboardCheck },
]

export function ClientPortalLayout() {
  const location = useLocation()
  const { isAuthenticated, logout } = useAuthStore()
  const [context, setContext] = useState<ClientPortalContext | null>(null)
  const [loading, setLoading] = useState(true)
  const [denied, setDenied] = useState(false)

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false)
      return
    }
    apiClient.get<ClientPortalContext>('/client-portal/me')
      .then((response) => setContext(response.data))
      .catch(() => setDenied(true))
      .finally(() => setLoading(false))
  }, [isAuthenticated])

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />
  }
  if (loading) {
    return <div className="min-h-screen grid place-items-center text-slate-500">Cargando portal…</div>
  }
  if (denied || !context) {
    return <Navigate to="/401" replace />
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            {context.logo_url ? (
              <img src={context.logo_url} alt="" className="h-10 w-10 rounded-lg object-contain" />
            ) : (
              <div className="grid h-10 w-10 place-items-center rounded-lg bg-emerald-700 font-bold text-white">E</div>
            )}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Portal Esterilav</p>
              <p className="font-semibold">{context.customer_name}</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-100"
          >
            <LogOut className="h-4 w-4" /> Salir
          </button>
        </div>
      </header>
      <nav className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-5">
          {links.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex shrink-0 items-center gap-2 border-b-2 px-3 py-3 text-sm font-medium ${
                  isActive ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-600'
                }`
              }
            >
              <Icon className="h-4 w-4" /> {label}
            </NavLink>
          ))}
        </div>
      </nav>
      <main className="mx-auto max-w-7xl p-5">
        <Outlet context={context} />
      </main>
    </div>
  )
}
